/**
 * Analytics React Context
 *
 * Provides analytics provider throughout the React component tree.
 */

import { createContext, useContext, useEffect, type ReactNode } from 'react'
import type { AnalyticsProvider, EventProperties, PageProperties, UserTraits } from './types'

/**
 * Noop provider for when analytics is disabled or not configured
 */
const noopProvider: AnalyticsProvider = {
  track: () => {},
  page: () => {},
  identify: () => {},
  reset: () => {},
  isReady: () => false,
}

/**
 * Analytics context
 */
const AnalyticsContext = createContext<AnalyticsProvider>(noopProvider)

/**
 * Analytics provider props
 */
export interface AnalyticsProviderProps {
  /** The analytics provider instance */
  provider: AnalyticsProvider
  /** Child components */
  children: ReactNode
}

/**
 * Analytics context provider component
 *
 * Wrap your app with this to provide analytics throughout the component tree.
 *
 * @example
 * ```tsx
 * import { AnalyticsProvider } from '@gjl/analytics';
 * import { createPostHogProvider } from '@gjl/analytics/posthog';
 *
 * const analytics = createPostHogProvider({ apiKey: 'phc_xxx' });
 *
 * function App() {
 *   return (
 *     <AnalyticsProvider provider={analytics}>
 *       <YourApp />
 *     </AnalyticsProvider>
 *   );
 * }
 * ```
 */
export function AnalyticsContextProvider({ provider, children }: AnalyticsProviderProps) {
  return <AnalyticsContext.Provider value={provider}>{children}</AnalyticsContext.Provider>
}

/**
 * Hook to access the analytics provider
 *
 * @example
 * ```tsx
 * function SignupButton() {
 *   const analytics = useAnalytics();
 *
 *   const handleClick = () => {
 *     analytics.track('signup_clicked');
 *   };
 *
 *   return <button onClick={handleClick}>Sign Up</button>;
 * }
 * ```
 */
// eslint-disable-next-line react-refresh/only-export-components -- hook is colocated with its provider by convention; dev-only Fast Refresh hint
export function useAnalytics(): AnalyticsProvider {
  return useContext(AnalyticsContext)
}

/**
 * Hook to track a page view on mount
 *
 * @param pageName - Name of the page
 * @param properties - Optional page properties
 *
 * @example
 * ```tsx
 * function AboutPage() {
 *   usePageView('About');
 *   return <div>About us...</div>;
 * }
 * ```
 */
// eslint-disable-next-line react-refresh/only-export-components -- hook is colocated with its provider by convention; dev-only Fast Refresh hint
export function usePageView(pageName: string, properties?: PageProperties) {
  const analytics = useAnalytics()

  useEffect(() => {
    analytics.page(pageName, properties)
  }, [analytics, pageName, properties])
}

/**
 * Hook to identify a user
 *
 * Automatically identifies the user when userId changes.
 * Resets analytics when userId becomes null/undefined.
 *
 * @param userId - User ID or null/undefined for logged out
 * @param traits - Optional user traits
 *
 * @example
 * ```tsx
 * function UserProvider({ children }) {
 *   const { user } = useAuth();
 *   useIdentify(user?.id, { email: user?.email, plan: user?.plan });
 *   return children;
 * }
 * ```
 */
// eslint-disable-next-line react-refresh/only-export-components -- hook is colocated with its provider by convention; dev-only Fast Refresh hint
export function useIdentify(userId: string | null | undefined, traits?: UserTraits) {
  const analytics = useAnalytics()

  useEffect(() => {
    if (userId) {
      analytics.identify(userId, traits)
    } else {
      analytics.reset()
    }
  }, [analytics, userId, traits])
}

/**
 * Hook to create a track function with preset properties
 *
 * Useful for components that need to track multiple events with common properties.
 *
 * @param baseProperties - Properties to include in all tracked events
 *
 * @example
 * ```tsx
 * function CheckoutFlow({ cartId }) {
 *   const track = useTrack({ cart_id: cartId, flow: 'checkout' });
 *
 *   return (
 *     <>
 *       <button onClick={() => track('step_1_completed')}>Next</button>
 *       <button onClick={() => track('payment_submitted', { method: 'card' })}>Pay</button>
 *     </>
 *   );
 * }
 * ```
 */
// eslint-disable-next-line react-refresh/only-export-components -- hook is colocated with its provider by convention; dev-only Fast Refresh hint
export function useTrack(baseProperties?: EventProperties) {
  const analytics = useAnalytics()

  return (event: string, properties?: EventProperties) => {
    analytics.track(event, { ...baseProperties, ...properties })
  }
}
